import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import './styles/Landlord.css';

const SkeletonStyles = () => (
    <style>{`
    .skeleton { position: relative; overflow: hidden; background: #e9eef5; }
    .skeleton::after { content: ''; position: absolute; top: 0; left: -150px; bottom: 0; width: 150px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent); animation: shimmer 1.2s infinite; }
    @keyframes shimmer { 0% { transform: translateX(0); } 100% { transform: translateX(300%); } }
  `}</style>
);

const base_url = 'https://api.homehive.co.zw/api';

const PropertyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [property, setProperty] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isUpdatingAvailability, setIsUpdatingAvailability] = useState(false);

    useEffect(() => {
        const fetchProperty = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${base_url}/properties/${id}/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                const data = response?.data;
                setProperty(data?.id ? data : (data?.results ? data.results[0] : data));
            } catch (error) {
                console.error('Error loading property:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to load property details',
                    icon: 'error',
                    confirmButtonColor: '#1a237e'
                }).then(() => navigate('/landlord/properties'));
            } finally {
                setIsLoading(false);
            }
        };
        if (id) fetchProperty();
    }, [id, navigate]);

    const formatPrice = (price) => {
        const num = typeof price === 'string' ? parseFloat(price) : Number(price);
        const safe = Number.isFinite(num) ? num : 0;
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safe);
    };

    const handleAvailabilityToggle = async () => {
        try {
            setIsUpdatingAvailability(true);
            const token = localStorage.getItem('token');
            
            // Toggle between 'available' and 'Occupied'
            const currentStatus = property.availability_status;
            const newStatus = currentStatus === 'available' ? 'occupied' : 'available';

            const response = await axios.patch(
                `${base_url}/properties/${id}/`,
                { availability_status: newStatus },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setProperty(prev => ({
                ...prev,
                availability_status: newStatus
            }));

            Swal.fire({
                title: 'Success!',
                text: `Property marked as ${newStatus}`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error updating availability:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to update property availability',
                icon: 'error',
                confirmButtonColor: '#1a237e'
            });
        } finally {
            setIsUpdatingAvailability(false);
        }
    };

    const handleEditProperty = () => {
        navigate(`/landlord/edit-property/${id}`);
    };

    const handleShare = () => {
        const shareData = {
            title: `${property?.property_type_display} in ${property?.location} \n\n${property?.description?.slice(0, 120)} \n\n${window.location.origin}/properties/${property?.id}`,
            url: `${window.location.origin}/properties/${property?.id}`
        };
        if (navigator.share) {
            navigator.share(shareData).catch(() => { });
            return;
        }
        const url = encodeURIComponent(`${window.location.origin}/properties/${property?.id}`);
        const text = encodeURIComponent(`${shareData.title}`);
        const options = [
            { label: 'WhatsApp', href: `https://wa.me/?text=${text}` },
            { label: 'Email', href: `mailto:?subject=${encodeURIComponent(shareData.title)}&body=${text}` },
            { label: 'SMS', href: `sms:?&body=${text}` },
        ];
        Swal.fire({
            title: 'Share Property',
            html: options.map(o => `<a href="${o.href}" target="_blank" rel="noreferrer" class="swal2-confirm swal2-styled" style="margin:6px;">${o.label}</a>`).join(''),
            showConfirmButton: false,
        });
    };

    if (isLoading) {
  return (
            <div className="add-property-wrapper">
                <SkeletonStyles />
                <div className="add-property-container">
                    <div className="property-detail-header">
                        <div className="skeleton" style={{ height: '40px', width: '60%', borderRadius: '6px', marginBottom: '12px' }} />
                        <div className="skeleton" style={{ height: '20px', width: '40%', borderRadius: '6px' }} />
                    </div>
                    <div className="property-detail-content">
                        <div className="row">
                            <div className="col-lg-8 mb-3">
                                <div className="skeleton" style={{ width: '100%', height: '420px', borderRadius: '8px' }} />
                            </div>
                            <div className="col-lg-4">
                                <div className="skeleton" style={{ height: '200px', width: '100%', borderRadius: '8px' }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!property) return null;

    const images = Array.isArray(property?.images) ? property.images : [];
    const cover = images[0]?.image ? (base_url + images[0].image) : '/images/homehive.jpg';
    const effectivePrice = property?.monthly_rental ?? property?.price_per_night ?? property?.price ?? 0;
    const isLodge = property?.property_type === 'lodge';
    const available = property?.availability_status === 'available';

    const featuresByType = () => {
        const type = (property?.property_type || '').toLowerCase();
        const items = [];
        if (type === 'apartment' || type === 'full_house' || type === 'house') {
            if (property?.bedrooms != null) items.push({ icon: 'fa-bed', label: `${property.bedrooms} bedrooms` });
            if (property?.type_of_bathroom) items.push({ icon: 'fa-bath', label: `${property.type_of_bathroom} bathroom` });
        } else if (type === 'room') {
            if (property?.type_of_bathroom != null) items.push({ icon: 'fa-bath', label: `${property.type_of_bathroom} bathroom` });
        } else if (type === 'boarding' || type === 'boarding_house') {
            if (property?.max_occupants) items.push({ icon: 'fa-users', label: `${property.max_occupants} Bed spaces(max)` });
            if (property?.gender_restriction) items.push({ icon: 'fa-users', label: `${property.gender_restriction} only.` });
        }
        return items;
    };

    const amenitiesByType = () => {
        const type = (property?.property_type || '').toLowerCase();
        const items = [];
        const pushIf = (cond, icon, label) => { if (cond) items.push({ icon, label }); };

        const flags = {
            wifi: property?.has_wifi,
            furnished: property?.is_furnished,
            parking: property?.has_parking_space,
            has_outdoor_space: property?.has_outdoor_space,
            security: property?.has_security,
            water: property?.water_source,
            power: property?.has_power_backup,
            backup_power: property?.power_backup,
            ac: property?.has_air_conditioning,
            kitchen: property?.has_kitchen,
            parking_slots: property?.parking_slots,
            has_pool: property?.has_pool,
            has_gym: property?.has_gym,
            has_cctv: property?.has_cctv,
            pets_allowed: property?.pets_allowed,
            has_electricity: property?.has_electricity,
            has_restaurant: property?.has_restaurant,
            has_bar: property?.has_bar,
            has_tv: property?.has_tv,
            has_study_area: property?.has_study_area,
        };

        if (type === 'apartment' || type === 'full_house' || type === 'house') {
            pushIf(flags.furnished, 'fa-couch', 'Furnished');
            pushIf(flags.parking, 'fa-square-parking', `Parking for ${flags.parking_slots || 1} vehicles`);
            pushIf(flags.has_outdoor_space, 'fa-seedling', 'Garden/Yard');
            pushIf(flags.security, 'fa-shield-halved', 'Security');
            pushIf(flags.water, 'fa-faucet', `${flags.water}`);
            pushIf(flags.power, 'fa-solar-panel', `Backup power (${flags.backup_power})`);
            pushIf(flags.ac, 'fa-snowflake', 'Air conditioning');
            pushIf(flags.kitchen, 'fa-kitchen-set', 'Fitted kitchen');
            pushIf(flags.has_pool, 'fa-swimming-pool', 'Swimming Pool');
            pushIf(flags.has_gym, 'fa-dumbbell', 'Gym');
            pushIf(flags.has_cctv, 'fa-video', 'CCTV');
            pushIf(flags.pets_allowed, 'fa-dog', 'Pets are allowed');
        } else if (type === 'room') {
            pushIf(flags.furnished, 'fa-couch', 'Furnished');
            pushIf(flags.parking, 'fa-square-parking', `Parking available`);
            pushIf(flags.water, 'fa-faucet', `${flags.water}`);
            pushIf(flags.has_electricity, 'fa-plug', 'Electricity');
            pushIf(flags.power, 'fa-solar-panel', `Backup power (${flags.backup_power})`);
        } else if (type === 'lodge') {
            pushIf(flags.wifi, 'fa-wifi', 'Wi‑Fi');
            pushIf(flags.ac, 'fa-snowflake', 'Air conditioning');
            pushIf(flags.parking, 'fa-square-parking', `Guest Parking`);
            pushIf(flags.security, 'fa-shield-halved', 'Security');
            pushIf(flags.has_restaurant, 'fa-utensils', 'Restaurant');
            pushIf(flags.has_bar, 'fa-wine-glass', 'Bar');
            pushIf(flags.has_tv, 'fa-tv', 'TV');
            pushIf(flags.has_pool, 'fa-swimming-pool', 'Swimming Pool');
        } else if (type === 'boarding' || type === 'boarding_house') {
            pushIf(flags.wifi, 'fa-wifi', 'Wi‑Fi');
            pushIf(flags.kitchen, 'fa-kitchen-set', 'Shared kitchen');
            pushIf(flags.has_study_area, 'fa-book', 'Study Area');
            pushIf(flags.security, 'fa-shield-halved', 'Security');
        }

        return items;
    };

    return (
        <div className="add-property-wrapper">
            <div className="add-property-container">
                {/* Header */}

                {/* Main Content */}
                <div style={{padding: '35px'}} className="property-detail-content">
                    <div style={{marginTop: '10px'}} className="property-title-section">
                        <h3 style={{color: 'navy'}}>{property?.title || property?.property_type_display || 'Property'}</h3>
                        <div className="property-location">
                            <i className="fas fa-map-marker-alt" />
                            <span>{property?.location}</span>
                        </div>
                    </div>
                    <div className="row">
                        {/* Left Column - Images */}
                        <div className="col-lg-7">
                            <div className="property-images-section">
                                {/* Main Image */}
                                <div className="main-image-container">
                                    <img
                                        src={images[selectedIndex]?.image ? (base_url + images[selectedIndex].image) : cover}
                                        alt={property?.title || `Image ${selectedIndex + 1}`}
                                        className="main-image"
                                    />
                                </div>

                                {/* Thumbnail Gallery */}
                                {images.length > 1 && (
                                    <div className="thumbnail-gallery">
                                        {images.map((img, idx) => (
                                            <button
                                                key={`thumb-${idx}`}
                                                type="button"
                                                onClick={() => setSelectedIndex(idx)}
                                                className={`thumbnail-btn ${selectedIndex === idx ? 'active' : ''}`}
                                            >
                                                <img
                                                    src={base_url + img.image}
                                                    alt={`Thumb ${idx + 1}`}
                                                    className="thumbnail-image"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Column - Property Info & Actions */}
                        <div className="col-lg-5">
                            <div className="property-info-card">
                                {/* Price & Availability */}
                                <div className="price-availability-section">
                                    <div className="price-info">
                                        <span className="price">{formatPrice(effectivePrice)}</span>
                                        <span className="price-period">{isLodge ? '/night' : '/month'}</span>
                                    </div>
                                    <span className={`availability-badge ${available ? 'available' : 'occupied'}`}>
                                        {property?.availability_status || 'Unknown'}
                                    </span>
                                </div>

                                {/* Availability Toggle */}
                                <div className="availability-control">
                                    <h3>Property Status</h3>
                                    <button
                                        className={`availability-toggle-btn ${available ? 'available' : 'occupied'}`}
                                        onClick={handleAvailabilityToggle}
                                        disabled={isUpdatingAvailability}
                                    >
                                        {isUpdatingAvailability ? (
                                            <div className="beat-loader">
                                                <div className="beat"></div>
                                                <div className="beat"></div>
                                                <div className="beat"></div>
                                            </div>
                                        ) : (
                                            <>
                                                <i className={`fas ${available ? 'fa-toggle-on' : 'fa-toggle-off'}`} />
                                                Mark as {available ? 'occupied' : 'available'}
                                            </>
                                        )}
                                    </button>
                                    <p className="availability-note">
                                        Toggle availability to control whether tenants can contact you about this property.
                                    </p>
                                </div>

                                {/* Property Stats */}
                                <div className="property-stats">
                                    <div className="stat-item">
                                        <i className="fas fa-eye"></i>
                                        <span>Views: {property?.views || 0}</span>
                                    </div>
                                    <div className="stat-item">
                                        <i className="fas fa-calendar-alt"></i>
                                        <span>Listed: {new Date(property?.created_at).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Property Details Section */}
                    <div className="property-details-section">
                        <div className="row">
                            <div className="col-lg-7">
                                <div className="details-card">
                                    {/* Description */}
                                    <div className="detail-section">
                                        <h3>Description</h3>
                                        <p>{property?.description || 'No description provided.'}</p>
                                    </div>

                                    {/* Key Features */}
                                    {featuresByType().length > 0 && (
                                        <div className="detail-section">
                                            <h3>Key Features</h3>
                                            <div className="features-grid">
                                                {featuresByType().map((f, idx) => (
                                                    <div key={`ft-${idx}`} className="feature-item">
                                                        <i className={`fas ${f.icon}`} />
                                                        <span>{f.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Amenities */}
                                    {amenitiesByType().length > 0 && (
                                        <div className="detail-section">
                                            <h3>Amenities</h3>
                                            <div className="amenities-grid">
                                                {amenitiesByType().map((a, idx) => (
                                                    <div key={`am-${idx}`} className="amenity-item">
                                                        <i className={`fas ${a.icon}`} />
                                                        <span>{a.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyDetail;